import React from 'react';
import { Record } from 'immutable';
import { useSelector } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useTooltip } from 'Wpds/components/tooltip/useTooltip';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import { useMeetingModeTracking } from 'Shared/tracking/use-meeting-mode-tracking';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Types
import { WorkspaceMeetingMode } from 'Shared/types/WorkspaceMeetingMode.type';

// Helpers
import WorkspaceMeetingModeHelper from 'Shared/model_helpers/WorkspaceMeetingMode.helper';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Components
import WpAvatarGroup from 'Wpds/components/avatar/WpAvatarGroup';
import AttendeeDetailsModal from '../AttendeeDetailsModal/AttendeeDetailsModal';
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpTooltipOverlay from 'Wpds/components/tooltip/WpTooltipOverlay';

// Styles
import './MeetingAttendees.scss';

type Props = {
  meetingModeSession: Record<WorkspaceMeetingMode>;
  showLabel?: boolean;
};

const MeetingAttendees = ({ meetingModeSession, showLabel = true }: Props) => {
  const tooltip = useTooltip();
  const { trackViewAttendees } = useMeetingModeTracking();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);
  const participantIds = WorkspaceMeetingModeHelper.getParticipantIds(meetingModeSession);

  const { isMedium } = useResponsiveScreenWidth({
    isAtLeast: true,
  });

  const { id: currentUserId } = useCurrentUser();

  const users = useSelector(state => UsersSelectors.getUsersByUserIds(state, { userIds: participantIds }))
    .valueSeq()
    .toJS();

  const filteredUsers = users
    .filter(user => user.id !== currentUserId)
    .filter(user => !user.firstName.includes('@'))
    .slice(0, 3);

  const handleOpenModal = () => {
    toggleIsModalOpen();
    trackViewAttendees();
  };

  const showUserNameSnippet = filteredUsers.length > 1;
  const baseClassName = 'MeetingAttendees';

  return (
    <>
      <div className={baseClassName} onClick={handleOpenModal}>
        <div className={`${baseClassName}__avatar-wrapper`} ref={tooltip.targetRef}>
          {isMedium ? (
            <WpAvatarGroup users={users} showAdditionalCount />
          ) : (
            <WpButton size="lg" variant="tertiary">
              <WpIcon name="user" />
            </WpButton>
          )}
        </div>
        {showLabel && <p className={`${baseClassName}__label`}>Attendees</p>}
      </div>
      <WpTooltipOverlay context={tooltip}>
        <p className={`${baseClassName}__tooltip-label`}>View all attendees</p>
        {showUserNameSnippet && (
          <p className={`${baseClassName}__tooltip-sub-label`}>
            Includes&nbsp;
            {filteredUsers.map((user, index) => {
              const isLast = index === filteredUsers.length - 1;
              const isFirst = index === 0;
              return (
                <span key={`attendee-tooltip-snippet-${user.id}`}>
                  {isLast && ' and '}
                  {user.firstName}
                  {!isLast && isFirst && filteredUsers.length > 2 && ', '}
                </span>
              );
            })}
          </p>
        )}
      </WpTooltipOverlay>
      {isModalOpen && <AttendeeDetailsModal isOpen={isModalOpen} toggleIsOpen={toggleIsModalOpen} users={users} />}
    </>
  );
};

export default MeetingAttendees;
